import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation,} from '@angular/core';

import {combineLatest, debounceTime, interval, Subject, Subscription, tap} from 'rxjs';
import { differenceInDays } from 'date-fns';

import {RouteEvent} from '../../../../../commons/models/domain/RouteEvent';
import {Vehicle} from '../../../../../commons/models/domain/Vehicle';
import {RouteDrawerService} from '@commons/services/map/drawer/route/route-drawer.service';
import {RouteService} from '@commons/services/route/route.service';
import {VehicleService} from '@commons/services/vehicle/vehicle.service';
import {RouteEventPoint} from '../../../../../commons/models/domain/RouteEventPoint';
import {NewWebSocketHttpService} from "@shared/websocket/new-web-socket-http.service";
import {NewVehiclesHttpService} from "@shared/services-http/new-vehicles-http.service";
import { RouteStats } from '@commons/models/domain/RouteStats';
import { MapService } from '@commons/services/map/map/map.service';
import { LanguageService } from '@shared/services/language.service';

interface GetRoutesSubscriptions {
    getRouteForDateSubsc: Subscription,
    showPointInMapSubsc?: Subscription,
}

@Component({
    selector: 'tk-vehicle-details-routes-tab',
    styleUrls: ['./vehicle-details-routes-tab.component.scss'],
    templateUrl: './vehicle-details-routes-tab.component.html',
    encapsulation: ViewEncapsulation.None
})
export class VehicleDetailsRoutesTabComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * Vehicle id
     */
    @Input() public vehicleId?: number;

    /**
     * Route events
     */
    public routes?: RouteEvent[];

    /**
     * Route date form control
     */
    // public RouteDate: FormControl;

    /**
     * Route available dates
     */
    public avaiableDates?: Date[];

    /**
     * Route sub
     */
    private routeSub: Subscription;

    private vehicleUpdate: Subscription;
    private lastUpdate: Date;

    /**
     * Subject for pushing vehicle value changes
     */
    private vehicleValueChangeSubject: Subject<Vehicle | undefined> = new Subject<Vehicle | undefined>();

    /**
     * Id of selected route event
     */
    public selectedRouteEventId?: number;

    /**
     * Flag indicating whether data is being fetched
     */
    public isLoading = true;

    public isVisibleCalendar: boolean;
    public initialDateCalendar: Date[] = [new Date(),new Date()];
    public selectedDatesInCalendar: Date[] = [];
    public onChangeDayCalendar$: Subject<Date[]> = new Subject();
    public vehicleRouteStatistics: RouteStats;

    public readonly langauge: string = this.languageService.current;
    private readonly ComponentSubsc: Subscription = new Subscription();
    private refreshRouteSub?: Subscription;

    /**
     * Component constructor
     * @param $fb form builder
     * @param $route route service
     * @param $routeDrawer route drawer service
     */
    constructor(
        private $route: RouteService,
        private $routeDrawer: RouteDrawerService,
        private $vehicleService: VehicleService,
        private $map: MapService,
        private newVehiclesHttpService: NewVehiclesHttpService,
        private newWebSocketHttpService: NewWebSocketHttpService,
        private languageService: LanguageService,
    ) {
        this.lastUpdate = new Date();

        this.vehicleUpdate = this.newWebSocketHttpService.updatedVehicles.subscribe(vehiclesList => {
                const current = vehiclesList.find(v => v.Id === this.vehicleId);
                if (!current)
                  return;

                if (current && current.ControlIndicators != null && current.LastPositionLat && current.LastPositionLon) {
                  var posParam = current.ControlIndicators.find(x => x.Tp == 22);

                  if (posParam != null && posParam.LV != null && posParam.LV.T != null) {
                    var utc = Date.parse(posParam.LV.T.toString());
                    var local = new Date(utc);

                    //update tylko jeśli pojazd jest w drodze (tj. pierwszy odcinek trasy ma typ 3)

                    if (local > this.lastUpdate && this.routes && this.routes.length > 0 && this.routes[0].T === 3) {
                      var pt = {} as RouteEventPoint;
                      pt.RouteEventId = this.routes[0].Id;
                      pt.Ln = current.LastPositionLon.toString();
                      pt.Lt = current.LastPositionLat.toString();
                      pt.T = posParam.LV.T;

                      this.lastUpdate = local;
                      this.routes[0].Points.push(pt);
                      // co pięć minut odświezamy trasę ale bez zooma

                      // this.$route.pointUpdateRequired.next(pt);
                      // GB jest to poprawka błędu polegającego na nie rysowaniu się lini bieżącej trasy
                      // nie mam pojęcia jak to wcześniej mogło działać bez tej linii
                      // nie wiem dlaczego ta linia nie rysuje się na bieżąco tylko co 3 odświerzenie danych nawet jeśli poniższa linia jest nad obecnym ifem i wywołuje się cały czas
                    }
                  }
                }
        });
    }

    public ngOnInit(): void {
        this.getInitialData();
        this.onChangeDayCalendar();
    }

    private refreshRouteEvery5Minutes(): void {
      if (this.refreshRouteSub) {
        this.refreshRouteSub.unsubscribe();
      }
      const MINUTES_5 = 300000;
      this.refreshRouteSub = interval(MINUTES_5).subscribe(() => {
        // console.log('Odświeżam trasę:', this.routes);
        this.$routeDrawer.setRoute(this.routes);
      });
    }

    private getInitialData(): void {
        const [from, to] = this.initialDateCalendar;
        this.getRoutesForDate(from, to);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // Push new vehicle
        const currentValue = <number>(changes['vehicleId'].currentValue);
        const previousValue = <number>(changes['vehicleId'].previousValue);
        const firstChange = changes['vehicleId'].firstChange;

        // When vehicles has not changed
        if (previousValue !== currentValue || firstChange === true) {
            this.vehicleValueChangeSubject.next(undefined);
        }
    }

    public ngOnDestroy(): void {
        this.ComponentSubsc.unsubscribe();
        this.refreshRouteSub?.unsubscribe();
        if (this.routeSub) {
            this.routeSub.unsubscribe();
        }
        if (this.vehicleUpdate) {
            this.vehicleUpdate.unsubscribe();
        }
        this.$vehicleService.setFollowVehicleStatus(true);
        this.$routeDrawer.resetRoute();
    }

    /**
     * Selects given segment
     * @param segment segment
     */
    public selectSegment(segment: RouteEvent) {
        if (this.selectedRouteEventId === segment.Id) {
            this.selectedRouteEventId = undefined;
            this.$routeDrawer.onClickPartOfRoute(undefined);
        } else {
            this.selectedRouteEventId = segment.Id;
            this.$routeDrawer.onClickPartOfRoute(segment);
        }
    }

    public onRouteEventDetailUpdate(route: RouteEvent) {
        const idx = this.routes?.findIndex(i => i.Id === route.Id);
        if (idx !== -1) {
            this.routes![idx!] = route;
        }
    }

    public onSelectDaysInCalendar(data: Date[]): void {
        this.selectedDatesInCalendar = [...data];
        this.onChangeDayCalendar$.next(data);
    }

    private onChangeDayCalendar(): void {
        let getRoutesSubscriptions: GetRoutesSubscriptions;
        const sub = this.onChangeDayCalendar$
        .pipe(
            tap(() => {
                getRoutesSubscriptions?.getRouteForDateSubsc?.unsubscribe();
                getRoutesSubscriptions?.showPointInMapSubsc?.unsubscribe();
                this.$routeDrawer.setRoute(undefined);
                this.$map.setMapLoaderState(true);
                this.isLoading = true;
            }),
            debounceTime(500),
        )
        .subscribe(([from, to]) => {
            getRoutesSubscriptions = this.getRoutesForDate(from, to);
        });
        this.ComponentSubsc.add(sub);
    }

    private getRoutesForDate(from: Date, to: Date): GetRoutesSubscriptions {
        let loadPointsForRouteSubsc: Subscription;

        const requests = combineLatest([
            this.$route.getRouteForDate$(from, to, this.vehicleId!),
            this.$route.getRouteStats$(this.vehicleId!, from, to),
        ]);

        const ifSelectedMoreThen2DaysRange = differenceInDays(to, from) > 1 ? true : false;

        const sub = requests.subscribe(
            ([routeData, routeStats]) => {
                if (!routeData?.length) {
                    this.$map.setMapLoaderState(false);
                }
                else {
                    loadPointsForRouteSubsc = this.loadPointsForRoute(routeData, ifSelectedMoreThen2DaysRange);
                }

                this.routes = routeData.reverse();
                this.vehicleRouteStatistics = { ...routeStats };
                this.isLoading = false;
                this.refreshRouteEvery5Minutes();
            }
        );
        this.ComponentSubsc.add(sub);
        this.ComponentSubsc.add(loadPointsForRouteSubsc!);

        return {
            getRouteForDateSubsc: sub,
            showPointInMapSubsc: loadPointsForRouteSubsc!,
        };
    }

    public trackByDayRoute(_: number, item: { date: string; routes: RouteEvent[] }): string {
        return item.date;
    }

    public trackByRoute(_: number, item: RouteEvent): number {
        return item.Id;
    }

    private loadPointsForRoute(routes: RouteEvent[], ifSelectedMoreThen2DaysRange?: boolean): Subscription {
      const ids = routes.map(x => x.Id);
      const sub = this.newVehiclesHttpService.getRouteDetailsList(ids!)
      .pipe(
        debounceTime(3000),
      )
      .subscribe(response => {
        response.forEach(responseRoute => {
          const matchedRoute = routes.find(x => x.Id === responseRoute.Id);
          matchedRoute!.Points = responseRoute.P;
        });
        this.$routeDrawer.setRoute(this.routes, ifSelectedMoreThen2DaysRange);
      });
      this.ComponentSubsc.add(sub);
      return sub;
    }

}
